import { RequestService } from 'Utils/Api';
import { logger } from 'Utils/Logger';
import { getDefaultConfig } from 'Utils/Store';
import { getShippingAddress } from 'Utils/Shipping';
import { getPromoCode } from 'Utils/PaymentMethods';
import { getSubscriptionParameters, getChangeSubscriptionType, getCouponAppliedByMethod } from 'Utils/Subscriptions';

const getPaypalScript = (clientId = 'sb') => {
  const SDK_URL = 'https://www.paypal.com/sdk/js?';
  const queryParams = new URLSearchParams({
    intent: 'authorize',
    'disable-funding': 'credit,card',
    commit: 'false',
    'client-id': clientId,
  });
  return `${SDK_URL}${queryParams.toString()}`;
};

const getPaypalOrderUrls = () => ({
  returnUrl: window.location.href,
  cancelUrl: window.location.href,
});

const getBuyerNote = (planName, orderPrice, purchaseMode) => {
  if (purchaseMode.isPlanPayment) {
    // default plan note
    return `${planName} Plan - $${orderPrice.toFixed(2)} (plus applicable taxes)`;
  }
  // default note
  return `${planName} - $${orderPrice.toFixed(2)} (plus applicable taxes)`;
};

export const getSubscriptionOrder = ({ plans, promoCode, checkout, purchaseMode }) => {
  const orderValue = promoCode.priceToBeCharged || plans.selectedPlanPrice;

  return {
    value: plans.isPlanSelected ? Math.ceil(orderValue * 100) : 0,
    buyerNote: getBuyerNote(plans.selectedPlan.planName, orderValue, purchaseMode),
    address: getShippingAddress(plans, checkout),
  };
};

const getPaypalSubscriptionRequest = (data) => {
  const { orderID, payerID, plans, isSkipDefaultPromo, promoCode, checkout } = data;
  const parameters = getSubscriptionParameters();
  const couponCode = getPromoCode(promoCode);
  const address = getShippingAddress(plans, checkout);
  return {
    orderID, // returned from paypal popup, order auth step
    payerID, // returned from paypal popup, order auth step, onapprove order
    serviceId: plans.selectedPlanId,
    promotionId: plans.selectedPlan.promotionID,
    isSkipDefaultPromo,
    couponCode,
    address,
    parameters,
    ...(couponCode && { appliedBy: getCouponAppliedByMethod() }),
  };
};

const getChangeSubscriptionRequest = (data) => {
  const { subscription, plans } = data;
  const { selectedPlan } = plans;
  const changeType = getChangeSubscriptionType({ subscription, selectedPlan });
  const payload = getPaypalSubscriptionRequest(data);
  return { ...payload, changeType };
};

export const loadPaypalSDK = () => {
  return new Promise((resolve, reject) => {
    if (window && typeof window.paypal !== 'undefined') {
      resolve();
      return;
    }
    const { paypalClientId } = getDefaultConfig();

    const PAYPAL_SDK_SCRIPT = getPaypalScript(paypalClientId);
    console.info('Loading PayPal SDK');
    const script = document.createElement('script');
    script.setAttribute('src', PAYPAL_SDK_SCRIPT);
    script.setAttribute('defer', 'defer');
    script.onload = () => {
      console.info('onload PayPal SDK');
      resolve();
    };
    script.onerror = (error) => reject(error);
    document.head.appendChild(script);
  });
};

const paypalApi = (url, payload) => {
  const { foxApi, accountApiKey } = getDefaultConfig();

  return RequestService.send({
    url: `${foxApi}/paypal${url}`,
    method: 'POST',
    data: payload,
    authenticate: true,
    customHeaders: { 'x-api-key': accountApiKey },
  }).then((res) => res && res.data);
};

export function createPayPalOrder(data) {
  const payload = { ...getPaypalOrderUrls(), ...data };
  // omitting address from log because personal information should not be sent 3rd parties
  const { address, ...filteredPayload } = payload;
  logger.apiRequest('paypal create order', filteredPayload);
  return paypalApi('/token', payload)
    .then((data) => {
      logger.apiResponse('paypal create order', data);
      return data.token;
    })
    .then((token) => {
      if (token) return token;

      logger.apiError('paypal create order failed');
      throw Error('Failed to create Paypal Order');
    })
    .catch((error) => {
      logger.apiError('paypal create order', null, error);
      return { errorCode: '', errorMessage: error.message };
    });
}

export function paypalAddSubscription(data) {
  const payload = getPaypalSubscriptionRequest(data);
  // omitting address from log because personal information should not be sent 3rd parties
  const { address, ...filteredPayload } = payload;
  logger.apiRequest('paypal add subscription', filteredPayload);
  return paypalApi('/capture', payload)
    .then((data) => {
      logger.apiResponse('paypal add subscription', data);
      if (!data || !data.orderId) {
        logger.apiError('paypal add subscription failed');
        throw Error('payment service failure: orderId missing on payment success');
      }
      return { isSuccess: true, orderID: data.orderId };
    })
    .catch((error) => {
      logger.apiError('paypal add subscription', null, error);
      return { isSuccess: false, errorCode: '', errorMessage: error.message };
    });
}

export function paypalChangeSubscription(data) {
  const payload = getChangeSubscriptionRequest(data);
  // omitting address from log because personal information should not be sent 3rd parties
  const { address, ...filteredPayload } = payload;
  logger.apiRequest('paypal change subscription', filteredPayload);
  return paypalApi('/changeService', payload)
    .then((data) => {
      logger.apiResponse('paypal change subscription', data);
      if (!data || !data.orderId) {
        logger.apiError('paypal change subscription failed');
        throw Error('payment service failure: orderId missing on payment success');
      }
      return { isSuccess: true, orderID: data.orderId };
    })
    .catch((error) => {
      logger.apiError('paypal change subscription', null, error);
      return { isSuccess: false, errorCode: '', errorMessage: error.message };
    });
}

export function paypalUpdatePaymentInfo(data) {
  const { orderID, payerID } = data;
  const payload = {
    orderID, // returned from paypal popup, order auth step
    payerID, // returned from paypal popup, order auth step, onapprove order
  };
  logger.apiRequest('paypal update payment', payload);
  return paypalApi('/updatePaymentInfo', payload)
    .then((data) => {
      logger.apiResponse('paypal update payment', data);
      return { isSuccess: true, orderID: data.orderId };
    })
    .catch((error) => {
      logger.apiError('paypal update payment', null, error);
      return { isSuccess: false, errorCode: '', errorMessage: error.message };
    });
}
